import { useEffect, useRef, useState } from 'react';
import { uiText } from '../../i18n';
import { studioMediaUrl, type StudioPreviewMediaSource } from './studio-preview-model';
import { FadeImg } from '../../ui/LateFade';

export function StudioPreviewMedia({
  media,
  held = false,
  autoplay = false,
  controls = true,
  label,
  loading = 'lazy',
  fade = false,
  className,
}: {
  media: StudioPreviewMediaSource;
  held?: boolean;
  autoplay?: boolean;
  controls?: boolean;
  label: string;
  /** Картинка первого экрана грузится сразу (eager), остальные — по подходу к окну */
  loading?: 'eager' | 'lazy';
  /** Догрузившаяся после первого кадра картинка проявляется (FadeImg), а не возникает — у плиток */
  fade?: boolean;
  /** Класс слоя прототипа (.ba-me — «ваше фото» слоя «до» листа шаблона) */
  className?: string;
}) {
  const video = useRef<HTMLVideoElement | null>(null);
  const heldPlayback = useRef(false);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const url = studioMediaUrl(media.url);
  useEffect(() => {
    const element = video.current;
    if (!element) return;
    const query = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
    const update = () => {
      if (held && autoplay && !query?.matches && document.visibilityState !== 'hidden') {
        heldPlayback.current = true;
        void element.play().catch(() => {});
      } else if (heldPlayback.current || document.visibilityState === 'hidden') {
        heldPlayback.current = false;
        element.pause();
      }
    };
    update();
    document.addEventListener('visibilitychange', update);
    query?.addEventListener?.('change', update);
    return () => {
      document.removeEventListener('visibilitychange', update);
      query?.removeEventListener?.('change', update);
    };
  }, [media.url, held, autoplay]);
  useEffect(() => {
    const element = video.current;
    return () => {
      heldPlayback.current = false;
      element?.pause();
    };
  }, [media.url]);
  if (!url || failedUrl === media.url) return <p role="status">{uiText('Пример сейчас недоступен.')}</p>;
  return media.type === 'video' ? (
    <video
      ref={video}
      className={className}
      src={url}
      poster={studioMediaUrl(media.poster)}
      muted
      playsInline
      loop={held}
      controls={controls}
      preload="metadata"
      aria-label={label}
      onError={() => {
        video.current?.pause();
        setFailedUrl(media.url);
      }}
    />
  ) : fade ? (
    <FadeImg
      className={className}
      src={url}
      alt={label}
      loading={loading}
      referrerPolicy="no-referrer"
      onError={() => setFailedUrl(media.url)}
    />
  ) : (
    <img
      className={className}
      src={url}
      alt={label}
      loading={loading}
      referrerPolicy="no-referrer"
      onError={() => setFailedUrl(media.url)}
    />
  );
}
